import React, { useEffect, useMemo, useRef, useState } from 'react'
import MyDriveIcons from '@/icons/MyDriveIcons'
import StarIcons from '@/icons/StarIcons'
import RecentIcons from '@/icons/RecentIcons'
import UserGroupIcons from '@/icons/UserGroupIcons'
import UploadIcons from '@/icons/UploadIcons'
import { Button, Popover, MenuList, TextField } from 'vnetwork-ui'
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom'
import toast from 'react-hot-toast'
import useAuthStore from '@/stores/useAuthStore'
import { useModal } from '@/contexts/modal'
import { CheckUploadItem } from '@/modules/Drive/services'
import { useDownloadFile } from '@/hooks/useDownload'
import { UploadConflictModalProps } from '@/modals/DriveModals/UploadConflictModal'
import useDriveStore, { Sections } from '@/stores/useDriveStore'
import NewButton, { customMenuItemStyle } from '../NewButton'
import ProgressBar from '../ProgressBar'
import { t } from 'i18next'
import useUploadFileV2 from '@/hooks/useUploadFileV2'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import SearchIcons from '@/icons/SearchIcons'
import {
    AddSuffix,
    CheckConflict,
    ListObjectKeys,
} from '../../helpers/UploadConflict'

const sections = [
    {
        id: Sections.MyDrive,
        title: 'my-drive-title',
        icon: <MyDriveIcons className="w-5 h-5" />,
        path: `/drive/${Sections.MyDrive}`, // Add path for navigation
    },
    {
        id: Sections.Starred,
        title: 'starred-files-title',
        icon: <StarIcons className="w-5 h-5" />,
        path: `/drive/${Sections.Starred}`,
    },
    {
        id: Sections.Recent,
        title: 'recent-file-title',
        icon: <RecentIcons className="w-5 h-5" />,
        path: `/drive/${Sections.Recent}`,
    },
    {
        id: Sections.Shared,
        title: 'shared-with-me-title',
        icon: (
            <UserGroupIcons className="flex items-center justify-center w-5 h-5" />
        ),
        path: `/drive/${Sections.Shared}`,
    },
]

const SEARCH_DEBOUNCE_DELAY = 600

const DriveHeaderV2: React.FC<{ children?: React.ReactNode }> = ({
    children,
}) => {
    const location = useLocation()
    const navigate = useNavigate()
    const { '*': path } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const [search, setSearch] = useState(searchParams.get('search') || '')

    // Manual debounce timer — gives us explicit cancel control on navigation
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Skip clearing search on the very first mount (URL may have ?search= on refresh)
    const isInitialMount = useRef(true)

    // Debounce: update URL params after user stops typing
    // setSearchParams intentionally excluded from deps — it's reference-unstable across
    // navigations and would re-trigger this effect with the stale search value

    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
        searchTimerRef.current = setTimeout(() => {
            setSearchParams(search.length > 0 ? { search } : undefined, {
                replace: true,
            })
        }, SEARCH_DEBOUNCE_DELAY)
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
        }
    }, [search])

    // On navigation: cancel any pending debounce timer, clear search input and URL param.
    // Handles both forward (folder click) and back navigation where history may restore ?search=.
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false
            return
        }
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current)
            searchTimerRef.current = null
        }
        setSearch('')
        setSearchParams(undefined, { replace: true })
    }, [location.pathname])

    const { uploadFiles, abortUpload, percent } = useUploadFileV2()

    // useMutationWithHandler
    const { mutateAsync: uploadFilesMutation } = useMutationWithHandler(
        {
            mutationFn: uploadFiles,
        },
        {
            reactQueryKeys: [
                QUERY_KEYS.DRIVE,
                QUERY_KEYS.DRIVE_FOLDER_EXPLORER,
            ],
            showSuccessToast: false,
            onError: (error) => {
                console.error('Upload error:', error)
            },
        }
    )

    const employee = useAuthStore((state) => state.employee)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const folderInputRef = useRef<HTMLInputElement>(null)
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])

    const { setModal } = useModal()
    const { setDownloadFile, setDownloadMultipleItems } = useDriveStore()

    const [isOpen, setIsOpen] = useState(false)
    const toggleMenu = () => setIsOpen((prev) => !prev)
    const isActive = (path: string) => location.pathname.endsWith(`${path}`)

    const uploadPrefix = useMemo(() => {
        let prefix = ''
        if (path && path.length > 0) {
            prefix = path
        } else if (employee?.employee_uuid) {
            prefix = String(employee.employee_uuid)
        }
        if (prefix.startsWith('pub/drives/')) {
            prefix = `drives/${prefix.slice('pub/drives/'.length)}`
        } else if (!prefix.startsWith('drives/')) {
            prefix = `drives/${prefix.replace(/^\/+/, '')}`
        }
        prefix = prefix.replace(/\/{2,}/g, '/')
        return prefix
    }, [path, employee?.employee_uuid])

    const {
        downloadFile,
        downloadMultipleItems,
        abortDownload,
        percent: percentDownload,
    } = useDownloadFile({
        onSuccess: () => {
            toast.success(t('drive.download-successful'))
        },
        onError: (error) => {
            console.error('Download failed:', error)
            toast.error(t('drive.download-failed'))
        },
        onCancel: () => {
            toast.error(t('drive.download-cancelled'))
        },
    })

    // Set the download file in the store so other components can use it
    useEffect(() => {
        setDownloadFile(downloadFile)
        setDownloadMultipleItems(downloadMultipleItems)
    }, [
        downloadFile,
        setDownloadFile,
        downloadMultipleItems,
        setDownloadMultipleItems,
    ])

    const handleAbortUpload = (fileName: string) => {
        abortUpload(fileName)
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            setSelectedFiles(Array.from(event.target.files))
            event.target.value = ''
        }
    }

    const handleUpload = async (extractedFiles: File[]) => {
        try {
            // transform files to FileToUpload
            const filesToUpload = extractedFiles.map((file) => ({
                uploadedFile: file,
                prefix: uploadPrefix,
            }))
            uploadFilesMutation(filesToUpload)
        } catch (error) {
            console.error('Upload error:', error)
        } finally {
            setSelectedFiles([])
        }
    }

    const handleCheckConflict = async (filesToUpload: File[]) => {
        try {
            const handleConflictCallback = (
                result: 'keepAll' | 'replace' | 'cancel'
            ) => {
                console.log('Result', result)
                if (result === 'cancel') return setSelectedFiles([])
                console.log('Existing objects: ', existingObjects)
                const finalFilesToUpload =
                    result === 'keepAll'
                        ? AddSuffix(
                              existingObjects,
                              existingObjects,
                              filesToUpload
                          )
                        : filesToUpload

                console.log(finalFilesToUpload)

                handleUpload(finalFilesToUpload)
            }

            const uploadedObjectKeys = filesToUpload.map((file) => file.name)

            // Call api to check for conflicts
            const isConflicted = await CheckConflict(
                uploadedObjectKeys,
                uploadPrefix
            )

            // If no conflict happens -> upload
            if (!isConflicted) {
                handleUpload(filesToUpload)
                return
            }

            let type = 'file' as 'file' | 'folder'
            if (filesToUpload.some((file) => file.name.includes('/'))) {
                type = 'folder'
            }

            // If conflicted, list all object keys in current path
            const existingObjects = await ListObjectKeys(uploadPrefix, type)

            // If current folder is empty -> upload
            if (existingObjects.length == 0) return handleUpload(filesToUpload)

            console.log(existingObjects)

            setModal<UploadConflictModalProps>(
                'DriveModals/UploadConflictModal/index',
                {
                    onResolve: handleConflictCallback,
                }
            )
        } catch (error: any) {
            console.error(error)
            toast.error(t('something-went-wrong'))
        }
    }

    useEffect(() => {
        if (!selectedFiles.length) return

        // format with heic
        const newImageFormats = ['heic', 'HEIC']
        const extractedFiles = selectedFiles?.map((file) => {
            const fileExtension = file.name.split('.').pop() || ''
            let fileType = file.type

            // Only set image/heic type for actual HEIC files that don't already have a type
            if (!file.type && newImageFormats.includes(fileExtension)) {
                fileType = 'image/heic'
            }

            return new File([file], file.webkitRelativePath || file.name, {
                type: fileType,
                lastModified: file.lastModified,
            })
        })

        handleCheckConflict(extractedFiles)
    }, [selectedFiles])

    const ButtonUploadFile = () => {
        const handleClick = async () => {
            try {
                const res = await CheckUploadItem(uploadPrefix)
                if (!res.result) {
                    toast.error(res.message)
                    return
                }
                fileInputRef.current?.click()
                setIsOpen(false)
            } catch (error: any) {
                if (error.response?.status === 403) {
                    toast.error(t('drive.no-upload-permission'))
                } else {
                    toast.error(error.message)
                }
            }
        }

        return (
            <button type="button" onClick={handleClick}>
                {t('upload-file-item')}
            </button>
        )
    }

    return (
        <div className="h-fit flex flex-col mt-2 px-5 py-5 gap-4 bg-white rounded-lg">
            <input
                ref={fileInputRef}
                type="file"
                hidden
                multiple
                onChange={handleChange}
            />
            <div className="flex flex-col gap-4 justify-between flex-wrap">
                <div className="flex gap-4">
                    <NewButton />
                    <Popover
                        visible={isOpen}
                        onClickOutside={() => setIsOpen(false)}
                        placement="bottom-start"
                        appendTo={document.body}
                        interactive
                        render={(attrs) => (
                            <div {...attrs}>
                                <MenuList
                                    onSelect={() => setIsOpen(false)}
                                    menuitems={[
                                        {
                                            children: <ButtonUploadFile />,
                                            className: customMenuItemStyle,
                                            icon: (
                                                <UploadIcons className="text-[#626262]" />
                                            ),
                                        },
                                        {
                                            children: (
                                                <label className="cursor-pointer">
                                                    <input
                                                        ref={folderInputRef}
                                                        type="file"
                                                        hidden
                                                        webkitdirectory=""
                                                        onChange={handleChange}
                                                    />
                                                    {t('upload-folder-item')}
                                                </label>
                                            ),
                                            className: customMenuItemStyle,
                                            icon: (
                                                <UploadIcons className="text-[#626262]" />
                                            ),
                                        },
                                    ]}
                                    size="medium"
                                />
                            </div>
                        )}
                    >
                        <div className="inline-block">
                            <Button
                                variant="outlined"
                                className="flex items-center gap-2"
                                onClick={() => toggleMenu()}
                            >
                                {t('upload')}
                                <span className="ml-1">
                                    <UploadIcons className="text-bg-white icon-small" />
                                </span>
                            </Button>
                        </div>
                    </Popover>
                    <TextField
                        endIcon={<SearchIcons />}
                        size="small"
                        className="md:min-w-[500px] max-w-full"
                        value={search}
                        onChange={(e) => setSearch(e.currentTarget.value)}
                    />
                </div>
                <div className="flex gap-2 justify-center">
                    <Button
                        onClick={() => navigate('root')}
                        className={`p-2 rounded-md transition w-20 ${
                            isActive('root')
                                ? 'bg-primary'
                                : '!bg-[#F9FBFF] text-primary'
                        }`}
                    >
                        {t('all')}
                    </Button>
                    {sections.map(({ id, icon, path, title }) => (
                        <Button
                            key={id}
                            onClick={() => navigate(path)}
                            className={`p-2 rounded-md transition w-20  ${
                                isActive(path)
                                    ? 'bg-primary'
                                    : '!bg-[#F9FBFF] text-primary'
                            }`}
                        >
                            {icon ? icon : title}
                        </Button>
                    ))}
                </div>
            </div>
            {children}
            <div className="fixed bottom-20 right-5 flex flex-col gap-2">
                <ProgressBar
                    percent={percent}
                    onCancel={handleAbortUpload}
                    type="upload"
                />
                <ProgressBar
                    percent={percentDownload}
                    onCancel={abortDownload}
                    type="download"
                />
            </div>
        </div>
    )
}

export default DriveHeaderV2
